import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Button } from '@core/components/ui';

/** Camera states driven by FileUploadZone. */
export const CAMERA_STATE = {
  IDLE: 'idle',
  CHECKING: 'checking',
  READY: 'ready',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
};

/**
 * UploadSourceMenu - Our own "Take photo / Gallery / File" sheet for phones that are not iOS.
 *
 * The native chooser of Android changes with the maker and the browser (some go straight to
 * the Files app, some drop the camera when its permission is denied), so we offer the three
 * sources ourselves. Gallery and file never need the camera permission, so they stay
 * available when the camera is denied or missing.
 */
export function UploadSourceMenu({
  open,
  onClose,
  t,
  cameraState,
  showCamera,
  showGallery,
  showFile,
  onTakePhoto,
  onPickGallery,
  onPickFile,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const isChecking = cameraState === CAMERA_STATE.CHECKING;
  const isReady = cameraState === CAMERA_STATE.READY;
  const isDenied = cameraState === CAMERA_STATE.DENIED;
  const isUnavailable = cameraState === CAMERA_STATE.UNAVAILABLE;
  const cameraProblem = isDenied || isUnavailable;

  const cameraLabel = isChecking
    ? t('onboarding.public.upload.menu.checking', 'Checking camera…')
    : isReady
      ? t('onboarding.public.upload.menu.openCamera', 'Open camera')
      : t('onboarding.public.upload.menu.takePhoto', 'Take photo');

  const cameraHint = isReady
    ? t('onboarding.public.upload.menu.cameraReady', 'Camera enabled. Tap to open it.')
    : t('onboarding.public.upload.menu.takePhotoHint', "We'll ask for permission to use the camera.");

  const sheet = (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding.public.upload.menu.title', 'Upload document')}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/70 cursor-pointer" aria-hidden="true" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-t-2xl bg-surface p-4 pb-6 shadow-lg space-y-3">
        <h3 className="text-base font-semibold text-default">
          {t('onboarding.public.upload.menu.title', 'Upload document')}
        </h3>

        {cameraProblem && (
          <div
            role="alert"
            className="rounded-lg border border-border-default bg-surface-alt p-3 space-y-1.5 text-sm text-muted"
          >
            {isDenied ? (
              <>
                <p className="font-medium text-default">
                  {t('onboarding.public.upload.menu.deniedTitle', 'The camera is turned off')}
                </p>
                <p>
                  {t(
                    'onboarding.public.upload.menu.deniedSteps',
                    'Turn it on in Settings → Apps → your browser → Permissions → Camera. If you use Chrome, you can also tap the lock next to the address → Permissions → Camera.',
                  )}
                </p>
                <p>
                  {t(
                    'onboarding.public.upload.menu.deniedWorkaround',
                    'Or take the photo with your phone camera and choose it from the gallery.',
                  )}
                </p>
              </>
            ) : (
              <p>
                {t(
                  'onboarding.public.upload.menu.unavailable',
                  "We couldn't access the camera. You can choose a photo from the gallery or a file.",
                )}
              </p>
            )}
          </div>
        )}

        {showCamera && !cameraProblem && (
          <div className="space-y-1">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isChecking}
              onClick={onTakePhoto}
              icon={<Icon icon="lucide:camera" className="size-5" />}
            >
              {cameraLabel}
            </Button>
            <p className="text-xs text-subtle text-center">{cameraHint}</p>
          </div>
        )}

        {showGallery && (
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={onPickGallery}
            icon={<Icon icon="lucide:image" className="size-5" />}
          >
            {t('onboarding.public.upload.menu.gallery', 'Choose from gallery')}
          </Button>
        )}

        {showFile && (
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={onPickFile}
            icon={<Icon icon="lucide:file-text" className="size-5" />}
          >
            {t('onboarding.public.upload.menu.file', 'Choose file (PDF)')}
          </Button>
        )}

        <Button variant="ghost" size="md" className="w-full" onClick={onClose}>
          {t('onboarding.public.upload.menu.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

UploadSourceMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  cameraState: PropTypes.oneOf(Object.values(CAMERA_STATE)).isRequired,
  showCamera: PropTypes.bool.isRequired,
  showGallery: PropTypes.bool.isRequired,
  showFile: PropTypes.bool.isRequired,
  onTakePhoto: PropTypes.func.isRequired,
  onPickGallery: PropTypes.func.isRequired,
  onPickFile: PropTypes.func.isRequired,
};
